import { Loader2, RotateCcw, TriangleAlert } from 'lucide-react'

import { fmtTimestamp } from '@/lib/http'
import type { TgeAuditResult } from '@/lib/tge'

import { BlockTitle } from './TgeReports'

/** TGE column → STAGIL column id ("field-123"); "" = the column is not migrated. */
export type ColumnOverrides = Record<string, string>

/** Where the map stands on the server for this grid → table. `by` null = nothing saved. */
export type MapSaveState =
  | { status: 'loading' }
  | { status: 'saving' }
  | { status: 'saved'; by: string | null; at: string | null }
  | { status: 'error'; op: 'load' | 'save'; error: string }

type Row = { col: string; target: string; manual: boolean }

/** TGE columns in audit order, each with the STAGIL column it goes to (manual choice first). */
function rowsOf(audit: TgeAuditResult, overrides: ColumnOverrides): Row[] {
  const cols = [...Object.keys(audit.column_map), ...audit.unmatched_columns]
  return cols.map((col) => {
    const manual = col in overrides
    return { col, manual, target: manual ? overrides[col] : (audit.column_map[col] ?? '') }
  })
}

/**
 * First reason the mapping cannot run: nothing mapped (the backend refuses such a run —
 * run #19 sent 685 empty tables), or one STAGIL column chosen for two TGE columns.
 */
export function columnMapProblem(audit: TgeAuditResult, overrides: ColumnOverrides): string | null {
  const rows = rowsOf(audit, overrides)
  if (rows.length > 0 && rows.every((r) => r.target === '')) {
    return 'No TGE column is mapped to a STAGIL column — pick at least one in the column map.'
  }
  const owner = new Map<string, string>()
  for (const r of rows) {
    if (r.target === '') continue
    const first = owner.get(r.target)
    if (first !== undefined) return `TGE columns ${first} and ${r.col} point to the same STAGIL column.`
    owner.set(r.target, r.col)
  }
  return null
}

function SaveLine({ save, where }: { save: MapSaveState; where: string }) {
  if (save.status === 'loading') {
    return <span className="inline-flex items-center gap-1"><Loader2 className="size-3 animate-spin" aria-hidden />Loading the map saved for {where}…</span>
  }
  if (save.status === 'saving') {
    return <span className="inline-flex items-center gap-1"><Loader2 className="size-3 animate-spin" aria-hidden />Saving…</span>
  }
  if (save.status === 'error') {
    return save.op === 'load'
      ? <span className="text-danger">The map saved for {where} did not load: {save.error}.</span>
      : <span className="text-danger">Not saved: {save.error}. Runs started from this page still use the map above.</span>
  }
  if (save.by === null) {
    return <>Nothing saved for {where} — columns match by name. A pick here is saved for every audit and run of it.</>
  }
  return (
    <>
      Saved for {where}
      {' · '}{save.by}{save.at && <>{' · '}{fmtTimestamp(save.at)}</>}
      {' — every audit and run of it uses this map.'}
    </>
  )
}

/**
 * TGE column → STAGIL column, one select per column. Columns match by name; grids whose TGE
 * columns carry internal ids (icost, icur, ...) match nothing and are mapped by hand here.
 * Each pick is saved for the grid → table: runs #20/#21 stopped on an empty map because it
 * lived only in the open page.
 */
export function ColumnMapEditor({ audit, overrides, onChange, onReset, disabled, save, where }: {
  audit: TgeAuditResult
  overrides: ColumnOverrides
  onChange: (col: string, target: string) => void
  onReset: () => void
  disabled: boolean
  save: MapSaveState
  where: string // "grid 25823 → table 5365"
}) {
  const rows = rowsOf(audit, overrides)
  const fields = audit.stagil_fields ?? []
  const mapped = rows.filter((r) => r.target !== '').length
  const owners = new Map<string, string[]>()
  for (const r of rows) {
    if (r.target !== '') owners.set(r.target, [...(owners.get(r.target) ?? []), r.col])
  }
  const manualCount = rows.filter((r) => r.manual).length

  return (
    <section>
      <BlockTitle
        aside={(
          <span className="flex items-center gap-3">
            <span className={`font-mono text-[11.5px] ${mapped === rows.length ? 'text-text-2' : 'text-warn'}`}>
              {mapped} of {rows.length} columns migrate
            </span>
            {manualCount > 0 && (
              <button
                type="button"
                onClick={onReset}
                disabled={disabled}
                className="inline-flex items-center gap-1 rounded-md border border-line-strong px-2 py-0.5 text-[11.5px] text-text-2 transition-colors hover:text-foreground disabled:opacity-60"
              >
                <RotateCcw className="size-3" aria-hidden />
                Match by name again
              </button>
            )}
          </span>
        )}
      >
        Column map
      </BlockTitle>

      {mapped === 0 && rows.length > 0 && (
        <p role="alert" className="mb-2 flex items-start gap-1.5 text-[12.5px] text-danger">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          No TGE column matches a STAGIL column by name. Pick the STAGIL column for each one below —
          migrate refuses to run while nothing is mapped.
        </p>
      )}

      <div className="-mx-4 max-h-[420px] overflow-y-auto border-y border-line">
        <table className="w-full table-fixed text-[12.5px]">
          <thead className="sticky top-0 bg-pane">
            <tr className="border-b border-line text-left text-[11px] font-medium uppercase tracking-[0.06em] text-text-3">
              <th scope="col" className="w-[30%] px-4 py-1.5 font-medium">TGE column</th>
              <th scope="col" className="px-2 py-1.5 font-medium">STAGIL column</th>
              <th scope="col" className="w-[120px] px-4 py-1.5 font-medium"><span className="sr-only">How it was chosen</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const clash = r.target !== '' && (owners.get(r.target)?.length ?? 0) > 1
              const others = clash ? owners.get(r.target)!.filter((c) => c !== r.col) : []
              const known = r.target === '' || fields.some((f) => f.id === r.target)
              return (
                <tr key={r.col} className="border-b border-line-row last:border-b-0">
                  <td className="truncate px-4 py-1 font-mono text-foreground" title={r.col}>{r.col}</td>
                  <td className="px-2 py-1">
                    <select
                      value={r.target}
                      onChange={(e) => onChange(r.col, e.target.value)}
                      disabled={disabled}
                      aria-label={`STAGIL column for TGE column ${r.col}`}
                      aria-invalid={clash || undefined}
                      className={[
                        'h-7 w-full min-w-0 rounded-md border bg-surface px-2 text-[12px] text-foreground disabled:opacity-60',
                        clash ? 'border-danger' : 'border-input',
                      ].join(' ')}
                    >
                      <option value="">— don't migrate —</option>
                      {!known && <option value={r.target}>{r.target}</option>}
                      {fields.map((f) => (
                        <option key={f.id} value={f.id}>{f.type ? `${f.title} · ${f.type}` : f.title}</option>
                      ))}
                    </select>
                    {clash && (
                      <p className="mt-0.5 text-[11.5px] text-danger">Also chosen for {others.join(', ')}.</p>
                    )}
                  </td>
                  <td className="px-4 py-1 text-[11.5px]">
                    {r.target === ''
                      ? <span className="text-warn">not migrated</span>
                      : r.manual
                        ? <span className="text-foreground">by hand</span>
                        : <span className="text-text-3">by name</span>}
                  </td>
                </tr>
              )
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-2 text-text-2">The grid has no rows, so there are no columns to map.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-1.5 text-[11.5px] leading-snug text-text-3" aria-live="polite">
        <SaveLine save={save} where={where} />
        {' '}Run the audit again after a change to recheck sizes.
      </p>
    </section>
  )
}
